/**
 * Marco, the Zonamento mascot (BRAND.md "Mascot"; plan mid-v6k8 decision 6).
 *
 * A port of marco() in docs/brand/mid-mascote-marco.html: the "i" of miD whose
 * pingo is a legend square carrying the eye (a square pupil) and every
 * expression; the stem is a plain bar that never changes. No base, no mouth.
 * viewBox 150x195: pingo x47 y8 56x56, stem x56 y76 38x110. `portrait` crops
 * to the pingo and the top of the stem (viewBox 25 0 100 100); it is the
 * default below 48 px, as on the proposal page.
 *
 * Motion (marco.css): the pingo hops once (280 ms) when `state` changes or when
 * `hop` is set, blinks in idle and found, scans while reading, and the found
 * zone marches. With prefers-reduced-motion no animation class is rendered.
 *
 * Read by MidMascot, MascotAvatar, MascotStage (behind NEW_BRAND) and by the
 * email art export (U7). Extra props (className, aria-label, data-*) land on
 * the <svg>.
 */
import React, { useEffect, useId, useRef, useState } from 'react';
import { BRAND_HEX as H } from '@/lib/brand';
import './marco.css';

const C = {
  navy: H.primary, yellow: H['habitacao-yellow'], red: H['equipamentos-red'], green: H['espacos-verdes-green'],
  alert: H.signal, paper: H.paper, tracing: H['surface-2'], grey: H.line, ink: H.ink, tint: H['equipamentos-tint'],
};

/** The five avatar colourways; ids match MASCOT_VARIANTS so buyers keep their pick. */
export const MARCO_WAYS = {
  av01: { name: 'Clássico', stem: C.navy, pingo: C.yellow },
  av02: { name: 'Curioso', stem: C.red, pingo: C.yellow },
  av03: { name: 'Sereno', stem: C.green, pingo: C.navy },
  av04: { name: 'Focado', stem: C.yellow, pingo: C.navy },
  av05: { name: 'Discreto', stem: 'hatch', pingo: C.grey },
};

export const MARCO_STATES = ['idle', 'reading', 'thinking', 'found', 'alert', 'midStatus', 'empty'];

/** Any state outside Marco's seven (legacy or misspelt) renders as idle. */
export const toMarcoState = (state) => (MARCO_STATES.includes(state) ? state : 'idle');

const PG = { x: 47, y: 8, s: 56, cx: 75, cy: 36 };
const STEM = { x: 56, y: 76, w: 38 };

const prefersReducedMotion = () =>
  typeof window !== 'undefined' && !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

export function MarcoSvg({ way = 'av01', state = 'idle', gaze = [0, 0], size = 150, portrait = size < 48, hop = false, style, ...rest }) {
  const w = MARCO_WAYS[way] || MARCO_WAYS.av01;
  const st = toMarcoState(state);
  const uid = useId().replace(/[^a-zA-Z0-9_-]/g, '');
  const hatchId = `marco-h${uid}`, clipId = `marco-c${uid}`;

  // Hop once per state change (not on mount); cleared when the animation ends.
  const [hopping, setHopping] = useState(false);
  const shown = useRef(st);
  useEffect(() => {
    if (shown.current !== st) { shown.current = st; setHopping(true); }
  }, [st]);
  const still = prefersReducedMotion();
  const anim = (cls, on) => (on && !still ? cls : undefined);

  const sw = size >= 96 ? 1.5 : 1;
  const e = { vectorEffect: 'non-scaling-stroke', stroke: 'currentColor', strokeWidth: sw };
  const empty = st === 'empty';
  const stemFill = empty ? C.tracing : w.stem === 'hatch' ? C.paper : w.stem;
  const stemH = portrait ? 140 : 110;
  const { x, y, s, cx, cy } = PG;
  const hatch = `url(#${hatchId})`;

  let pingo;
  if (st === 'midStatus') {
    const h = s / 2;
    pingo = (
      <>
        <rect x={x} y={y} width={h} height={h} fill={C.yellow} {...e} />
        <rect x={x + h} y={y} width={h} height={h} fill={C.red} {...e} />
        <rect x={x} y={y + h} width={h} height={h} fill={C.green} {...e} />
        <rect x={x + h} y={y + h} width={h} height={h} fill={C.paper} {...e} />
        <rect x={x + h} y={y + h} width={h} height={h} fill={hatch} />
      </>
    );
  } else if (st === 'thinking') {
    pingo = (
      <>
        <rect x={x} y={y} width={s} height={s} fill={C.paper} {...e} strokeDasharray="4 3" />
        <rect x={x} y={y} width={s} height={s} fill={hatch} />
      </>
    );
  } else {
    pingo = <rect x={x} y={y} width={s} height={s} fill={empty ? C.grey : st === 'alert' ? C.alert : w.pingo} {...e} />;
  }

  // The eye
  let eye;
  if (empty) {
    eye = <path d={`M${cx - 13} ${cy}H${cx + 13}`} {...e} />;
  } else if (st === 'reading') {
    eye = (
      <>
        <rect x={x + 6} y={y + 6} width={s - 12} height={s - 12} fill={C.paper} {...e} />
        <g clipPath={`url(#${clipId})`}>
          {[C.yellow, C.tint, C.tracing].map((f, i) => (
            <React.Fragment key={f}>
              <rect x={x + 11} y={y + 12 + i * 11} width="8" height="8" fill={f} stroke={C.ink} strokeWidth="1" vectorEffect="non-scaling-stroke" />
              <path d={`M${x + 23} ${y + 16 + i * 11}H${x + (i === 1 ? 38 : 44)}`} stroke={C.ink} strokeWidth="2" vectorEffect="non-scaling-stroke" />
            </React.Fragment>
          ))}
          <rect className={anim('marco-scan', true)} x={x + 6} y={y + 8} width={s - 12} height="2" fill={C.navy} />
        </g>
      </>
    );
  } else {
    const dark = w.pingo === C.navy && !['alert', 'thinking', 'midStatus'].includes(st);
    const ps = st === 'alert' || st === 'found' ? 22 : 18;
    const reach = (s - ps) / 2 - 8;
    const [gx, gy] = st === 'thinking' ? [0.8, -0.8] : gaze;
    const clamp = (v) => Math.max(-1, Math.min(1, Number(v) || 0));
    const dx = clamp(gx) * reach, dy = clamp(gy) * reach * 0.8;
    eye = (
      <g transform={`translate(${dx.toFixed(1)} ${dy.toFixed(1)})`}>
        <rect x={cx - ps / 2} y={cy - ps / 2} width={ps} height={ps} fill={dark ? C.paper : C.ink} />
        {size >= 96 && <rect x={cx - ps / 2 + 3} y={cy - ps / 2 + 3} width="5" height="5" fill={dark ? C.navy : C.paper} />}
      </g>
    );
  }

  return (
    <svg
      viewBox={portrait ? '25 0 100 100' : '0 0 150 195'}
      width={size}
      height={portrait ? size : Math.round((size * 195) / 150)}
      role="img"
      aria-label="Marco"
      data-state={st}
      // A portrait is a crop: the 140-high stem must not spill out of the square.
      style={{ color: `var(--brand-ink, ${C.ink})`, overflow: portrait ? 'hidden' : 'visible', ...style }}
      {...rest}
    >
      <defs>
        <pattern id={hatchId} width="7" height="7" patternUnits="userSpaceOnUse">
          <path d="M-1 1l2-2M0 7l7-7M6 8l2-2" stroke={C.ink} strokeWidth="1" opacity=".5" />
        </pattern>
        <clipPath id={clipId}><rect x={x} y={y} width={s} height={s} /></clipPath>
      </defs>

      {/* Stem: the body of the i, never changes */}
      <rect x={STEM.x} y={STEM.y} width={STEM.w} height={stemH} fill={stemFill} {...e} />
      {(empty || w.stem === 'hatch') && <rect x={STEM.x} y={STEM.y} width={STEM.w} height={stemH} fill={hatch} />}

      {/* Pingo: face, eye and every expression */}
      <g
        className={anim('marco-hop', hop || hopping)}
        onAnimationEnd={(ev) => ev.animationName === 'marco-hop' && setHopping(false)}
      >
        {st === 'found' && (
          <rect
            className={anim('marco-march', true)}
            x={x - 9} y={y - 7} width={s + 18} height={s + 18}
            strokeWidth="1.5" strokeDasharray="6 5" vectorEffect="non-scaling-stroke"
            style={{ fill: `var(--brand-primary, ${C.navy})`, fillOpacity: 0.12, stroke: `var(--brand-primary, ${C.navy})` }}
          />
        )}
        <g className={anim('marco-blink', st === 'idle' || st === 'found')}>{pingo}{eye}</g>
      </g>
    </svg>
  );
}

export default MarcoSvg;
